import { useCallback, useEffect, useState } from 'react';
import type { PilotLanguageEndorsement } from '../../types/pilot';
import {
  usePilotLanguageEndorsements,
  type CreateLanguageEndorsementInput,
} from '../../hooks/usePilotLanguageEndorsements';
import type { PilotTarget } from '../../hooks/usePilotRatings';
import { formatError } from '../../lib/formatError';

type Level = 1 | 2 | 3 | 4 | 5 | 6;
const LEVELS: Level[] = [1, 2, 3, 4, 5, 6];

interface Props {
  target: PilotTarget;
}

interface Draft {
  language_code: string;
  language_proficiency_level: Level;
  language_valid_until: string;
  language_assessing_body: string;
}

const EMPTY: Draft = {
  language_code: 'en',
  language_proficiency_level: 4,
  language_valid_until: '',
  language_assessing_body: '',
};

export default function PilotLanguagesTable({ target }: Props) {
  const api = usePilotLanguageEndorsements();
  const [rows, setRows] = useState<PilotLanguageEndorsement[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.list(target));
      setError(null);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  }, [target, api]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function addEndorsement() {
    if (!draft.language_code.trim()) {
      setError('Language code is required');
      return;
    }
    setSaving(true);
    try {
      const input: CreateLanguageEndorsementInput = {
        target,
        language_code: draft.language_code.trim().toLowerCase(),
        language_proficiency_level: draft.language_proficiency_level,
        language_valid_until: draft.language_valid_until || null,
        language_assessing_body: draft.language_assessing_body.trim() || null,
      };
      await api.create(input);
      setDraft(EMPTY);
      setError(null);
      await refresh();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSaving(false);
    }
  }

  async function removeRow(id: string) {
    if (!confirm('Delete this language endorsement?')) return;
    try {
      await api.remove(id);
      await refresh();
    } catch (err) {
      setError(formatError(err));
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded border border-gray-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Language</th>
              <th className="px-3 py-2 text-left font-medium">ICAO level</th>
              <th className="px-3 py-2 text-left font-medium">Valid until</th>
              <th className="px-3 py-2 text-left font-medium">Assessing body</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-gray-500">
                  No language endorsements yet.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100">
                <td className="px-3 py-2 font-medium uppercase">{r.language_code}</td>
                <td className="px-3 py-2">{r.language_proficiency_level}</td>
                <td className="px-3 py-2">
                  {r.language_valid_until ?? (r.language_proficiency_level === 6 ? '∞' : '—')}
                </td>
                <td className="px-3 py-2">{r.language_assessing_body ?? '—'}</td>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() => removeRow(r.id)}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded border border-gray-200 bg-gray-50 p-3">
        <div className="mb-2 text-sm font-medium text-gray-700">Add language endorsement</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-2 gap-y-3">
          <FormField label="Language code">
            <input
              type="text"
              placeholder="en, fr"
              value={draft.language_code}
              onChange={(e) => setDraft({ ...draft, language_code: e.target.value })}
              className="w-full rounded border border-gray-300 px-2 py-1 text-sm uppercase"
              maxLength={10}
            />
          </FormField>
          <FormField label="ICAO level">
            <select
              value={draft.language_proficiency_level}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  language_proficiency_level: Number(e.target.value) as Level,
                })
              }
              className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
            >
              {LEVELS.map((l) => (
                <option key={l} value={l}>
                  Level {l}
                </option>
              ))}
            </select>
          </FormField>
          <FormField
            label="Valid until"
            hint={draft.language_proficiency_level === 6 ? 'Level 6: open-ended' : undefined}
          >
            <input
              type="date"
              value={draft.language_valid_until}
              onChange={(e) => setDraft({ ...draft, language_valid_until: e.target.value })}
              className="w-full rounded border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-100"
              disabled={draft.language_proficiency_level === 6}
              title={
                draft.language_proficiency_level === 6 ? 'Level 6 is typically open-ended' : ''
              }
            />
          </FormField>
          <FormField label="Assessing body">
            <input
              type="text"
              placeholder="Organisation name"
              value={draft.language_assessing_body}
              onChange={(e) => setDraft({ ...draft, language_assessing_body: e.target.value })}
              className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </FormField>
        </div>
        <div className="mt-2">
          <button
            type="button"
            disabled={saving}
            onClick={addEndorsement}
            className="rounded bg-[#0B2C45] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#0B2C45]/90 disabled:opacity-50"
          >
            {saving ? 'Adding…' : 'Add endorsement'}
          </button>
        </div>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}
    </div>
  );
}

function FormField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 flex items-baseline justify-between text-xs font-medium text-gray-600">
        <span>{label}</span>
        {hint && <span className="text-[10px] font-normal text-gray-500">{hint}</span>}
      </span>
      {children}
    </label>
  );
}
